import { createContext, FC, ReactNode, useContext, useMemo } from 'react';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { useFormat } from 'common/hooks';
import { DispatchFormat, Format, UseFormatValues } from 'common/types/format';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY } from 'pages/Firms/common/constants';
import { useFirmFundFilter, UseFirmFundFilterReturn } from 'pages/Firms/hooks';

// Shared currency/units format state lives here so the tertiary-nav currency selector
// (rendered by FirmSummaryWorkspace) and the firm summary spreadsheet (built deep inside
// FirmSummaryBody → useFirmSummaryTab) drive the same reducer instance.
export interface IntelligenceContextValue extends UseFirmFundFilterReturn {
  format: Format;
  formatDispatch: DispatchFormat;
}

const IntelligenceContext = createContext<IntelligenceContextValue | null>(null);

interface IntelligenceProviderProps {
  children: ReactNode;
}

export const IntelligenceProvider: FC<IntelligenceProviderProps> = ({ children }) => {
  const fundFilter = useFirmFundFilter();

  // measurementDate is omitted: useFormat resolves the measurement date id from the
  // MEASUREMENT_DATE_FILTER page filter that useFirmMeasurementDateFilter registers.
  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const value = useMemo<IntelligenceContextValue>(
    () => ({
      fundOptions: fundFilter.fundOptions,
      isAllFundsSelected: fundFilter.isAllFundsSelected,
      isFundSelectionPending: fundFilter.isFundSelectionPending,
      isLoadingFunds: fundFilter.isLoadingFunds,
      selectedFund: fundFilter.selectedFund,
      selectedFundId: fundFilter.selectedFundId,
      setSelectedFund: fundFilter.setSelectedFund,
      format,
      formatDispatch,
    }),
    [
      fundFilter.fundOptions,
      fundFilter.isAllFundsSelected,
      fundFilter.isFundSelectionPending,
      fundFilter.isLoadingFunds,
      fundFilter.selectedFund,
      fundFilter.selectedFundId,
      fundFilter.setSelectedFund,
      format,
      formatDispatch,
    ]
  );

  return <IntelligenceContext.Provider value={value}>{children}</IntelligenceContext.Provider>;
};

export const useIntelligenceContext = (): IntelligenceContextValue => {
  const value = useContext(IntelligenceContext);
  if (value === null) {
    throw new Error('useIntelligenceContext must be used within an IntelligenceProvider');
  }
  return value;
};
